import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { TextButton } from 'js/design_system/TextButton';
import { trackClick } from 'js/utils/trackClick';
import { HeroThumbnailStack } from 'js/liswp_blocks/shared/HeroThumbnailStack';
import { authorLine } from 'js/liswp_blocks/shared/authorLine';

import { lazyOpenLoginModal } from 'js/single_work/loswp/download_modal/lazyOpenLoginModal';
import type { MoreActionsMenuProps } from 'js/single_work/loswp/MoreActionsMenu';

const BOOKMARK_SOURCE = 'loswp--bookmark-button--work-card';
const MORE_OPTIONS_SOURCE = 'loswp--more-options--work-card';

const Header = styled.div<{ spacing?: 'compact' }>`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  gap: var(--spacing-md-40, 40px);
  width: 100%;
  padding-bottom: var(--spacing-sm-32, 32px);
  text-align: left;

  &.spacing-compact {
    padding-bottom: var(--spacing-sm-24, 24px);
  }

  @include breakpoint(small, medium) {
    gap: var(--spacing-sm-24, 24px);
    padding-bottom: var(--spacing-sm-24, 24px);
  }
`;

const ThumbWrap = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  flex-shrink: 0;
  width: 164px;
  height: 221.44px;

  @include breakpoint(small, medium) {
    width: 115px;
    height: 154.27px;
  }
`;

const TextColumn = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;
`;

const Title = styled.h1`
  @import 'stylesheets/design_system/breakpoint';

  margin: 0;
  padding-bottom: var(--spacing-xs-16, 16px);
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-sans-serif-xl-size, 42px);
  line-height: var(--type-sans-serif-xl-line-height, 50px);
  font-weight: var(--type-sans-serif-xl-font-weight, 400);
  color: var(--text-primary, #141413);

  @include breakpoint(small, medium) {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: var(--type-body-md-bold-size, 16px);
    line-height: var(--type-body-md-bold-line-height, 20px);
    font-weight: var(--type-body-md-bold-font-weight, 600);
  }
`;

const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
`;

const MetaLine = styled.p`
  @import 'stylesheets/design_system/breakpoint';

  margin: 0;
  font-family:
    var(--type-font-family-sans-serif, 'DM Sans'), Helvetica, Arial, sans-serif;
  font-size: var(--type-body-md-size, 16px);
  line-height: var(--type-body-md-line-height, 20px);
  color: var(--text-primary, #141413);

  @include breakpoint(small, medium) {
    font-size: var(--type-body-sm-size, 14px);
    line-height: var(--type-body-sm-line-height, 20px);
  }
`;

const AuthorsFull = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  @include breakpoint(small, medium) {
    display: none;
  }
`;

const AuthorsCompact = styled.span`
  @import 'stylesheets/design_system/breakpoint';

  display: none;

  @include breakpoint(small, medium) {
    display: inline;
  }
`;

const ActionRow = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  align-items: center;
  gap: var(--spacing-sm-24, 24px);
  padding-top: var(--spacing-xs-16, 16px);

  @include breakpoint(small, medium) {
    display: none;
  }
`;

export type HeroWorkCardHeaderProps = {
  workId: number;
  paperTitle: string;
  authors: Array<{ displayName: string }>;
  publicationName?: string;
  publicationYear?: number;
  thumbnailUrl?: string;
  showBookmark?: boolean;
  onThumbnailClick?: () => void;
  thumbnailClickLabel?: string;
  bottomPadding?: 24 | 32;
};

export const HeroWorkCardHeader: React.FC<HeroWorkCardHeaderProps> = ({
  workId,
  paperTitle,
  authors,
  publicationName,
  publicationYear,
  thumbnailUrl,
  showBookmark = false,
  onThumbnailClick,
  thumbnailClickLabel,
  bottomPadding = 32,
}) => {
  const [MoreActionsMenu, setMoreActionsMenu] =
    useState<React.ComponentType<MoreActionsMenuProps> | null>(null);

  useEffect(() => {
    let cancelled = false;
    import('js/single_work/loswp/MoreActionsMenu')
      .then((module) => {
        if (!cancelled) setMoreActionsMenu(() => module.MoreActionsMenu);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const workUrl = routes.show_work_url({ id: workId });
  const publicationLine = [publicationYear, publicationName]
    .filter(Boolean)
    .join(', ');

  const onBookmark = () => {
    trackClick({
      source: BOOKMARK_SOURCE,
      entityId: workId,
      extra: { source_work_id: workId },
    });
    lazyOpenLoginModal({
      postLoginRedirectUrl: workUrl,
      clickTrackingContext: {
        entityId: workId,
        extra: { source_work_id: workId },
      },
      registrationParams: { user_source: 'download' },
    });
  };

  const moreOptionsTrigger = (
    <TextButton size="sm" icon="more_horiz">
      More options
    </TextButton>
  );

  return (
    <Header spacing={bottomPadding === 24 ? 'compact' : undefined}>
      <ThumbWrap>
        <HeroThumbnailStack
          thumbnailUrl={thumbnailUrl}
          hasPdf
          onClick={onThumbnailClick}
          clickLabel={thumbnailClickLabel}
        />
      </ThumbWrap>
      <TextColumn>
        <Title>{paperTitle}</Title>
        <MetaBlock>
          <MetaLine>
            <AuthorsFull>
              {authors.map((author) => author.displayName).join(', ')}
            </AuthorsFull>
            <AuthorsCompact>{authorLine(authors)}</AuthorsCompact>
          </MetaLine>
          {publicationLine && <MetaLine>{publicationLine}</MetaLine>}
        </MetaBlock>
        <ActionRow>
          {showBookmark && (
            <TextButton size="sm" icon="bookmark" onClick={onBookmark}>
              Bookmark
            </TextButton>
          )}
          {MoreActionsMenu ? (
            <MoreActionsMenu
              workId={workId}
              redirectUrl={workUrl}
              trackClickOptions={{
                source: MORE_OPTIONS_SOURCE,
                entityId: workId,
              }}
              trigger={moreOptionsTrigger}
            />
          ) : (
            moreOptionsTrigger
          )}
        </ActionRow>
      </TextColumn>
    </Header>
  );
};

export default HeroWorkCardHeader;
